import { FC, PropsWithChildren, useMemo } from 'react';
import {
  CommonPageDataContext,
  EMPTY_INTERESTS,
} from './CommonPageDataContext.ts';
import { useQuery } from '@tanstack/react-query';
import {
  fetchInterests,
  INTEREST_QUERY_KEY,
} from '@moment/net/classic-fotoscapes/fetch-interests.ts';
import {
  EnhancedInterestResponse,
  IInterestResponse,
} from '@pages/Root/services/api/interest/interest-model-definitions.ts';
import { useTranslation } from 'react-i18next';

export type AdSourceType = 'none' | 'gam' | 'dtx' | 'dtx,gam';

export interface RuntimeConfig {
  property: string;
  brandGroupId: string;
  utmSource: string;
  appVersion?: string;
  advId?: string;
  adSource: AdSourceType;
  partner?: string;
  appBundle?: string;
  appName?: string;
  numberOfPages?: number;
  shuffleSeed: string;
  LOCATION_API_BASE_URL: string;
  EVENT_COLLECTOR_BASE_URL: string;
}

declare global {
  interface Window {
    __RUNTIME_CONFIG__?: RuntimeConfig;
  }
}

const getRuntimeConfig = (): RuntimeConfig => {
  if (typeof window !== 'undefined' && window.__RUNTIME_CONFIG__) {
    return window.__RUNTIME_CONFIG__;
  }
  // Fallback for environments where the object is not injected (e.g., Storybook)
  return {
    property: 'dt',
    brandGroupId: 'fotoscapes-default',
    utmSource: '',
    partner: 'dt',
    adSource: 'gam',
    numberOfPages: 4,
    shuffleSeed: '0',
    LOCATION_API_BASE_URL: '',
    EVENT_COLLECTOR_BASE_URL: '',
  };
};

export const WithCommonPageData: FC<
  Partial<PropsWithChildren> & { runtimeConfig: RuntimeConfig }
> = ({ children, runtimeConfig }) => {
  const { i18n } = useTranslation();
  const lang = i18n.language;

  // The runtime config is loaded once per page load and does not change.
  // Using an empty dependency array ensures this runs only on mount,
  // preventing re-renders caused by unstable references to window.__RUNTIME_CONFIG__.
  const {
    brandGroupId,
    utmSource,
    appVersion,
    partner,
    advId,
    adSource,
    appName,
    appBundle,
    shuffleSeed,
    LOCATION_API_BASE_URL,
    EVENT_COLLECTOR_BASE_URL,
  } = useMemo(() => runtimeConfig || getRuntimeConfig(), []);

  const commonQueryConstruct = {
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: Infinity,
  };

  const interestsQueryConstruct = {
    ...commonQueryConstruct,
    queryKey: [lang, INTEREST_QUERY_KEY],
    queryFn: fetchInterests,
    select: (
      fetchedData: IInterestResponse | EnhancedInterestResponse | null
    ) => {
      if (!fetchedData) {
        return null;
      }
      if (fetchedData instanceof EnhancedInterestResponse) {
        return fetchedData;
      }
      return new EnhancedInterestResponse(fetchedData as IInterestResponse);
    },
  };

  const { data: interestData } = useQuery<EnhancedInterestResponse | null>(
    interestsQueryConstruct
  );

  const contextValue = useMemo(
    () => ({
      interests: interestData || EMPTY_INTERESTS,
      utmSource,
      brandGroupId,
      appVersion,
      partner,
      advId,
      adSource,
      appName,
      appBundle,
      shuffleSeed,
      LOCATION_API_BASE_URL,
      EVENT_COLLECTOR_BASE_URL,
    }),
    [
      interestData,
      utmSource,
      brandGroupId,
      appVersion,
      partner,
      advId,
      adSource,
      shuffleSeed,
      LOCATION_API_BASE_URL,
      EVENT_COLLECTOR_BASE_URL,
    ]
  );

  return (
    <CommonPageDataContext.Provider value={contextValue}>
      {children}
    </CommonPageDataContext.Provider>
  );
};
